import { token } from '@atlaskit/tokens';
import { readableTextColor as baseReadableTextColor, toHexColor } from '@atlassian/color-picker';
import { generateThemeBranding } from '@atlassian/csm-colour-theme-generator/generate-theme-branding';
import { fireErrorAnalytics } from '@atlassian/error-handling';

import {
	DEFAULT_BRANDING_COLOR,
	HUB_PRIMARY_COLOR_CSS_VAR,
	HUB_PRIMARY_LINK_COLOR_CSS_VAR,
	HUB_PRIMARY_TEXT_COLOR_CSS_VAR,
} from '../../constants';

const readableTextColor = (color: string): string => {
	return baseReadableTextColor(color, '@atlassian/company-hub-common');
};

export const setHubBrandingStyle = ({
	primaryColor,
	targetElement,
}: {
	primaryColor?: string;
	targetElement?: HTMLDivElement | null;
}) => {
	const primaryColorToUse = primaryColor || DEFAULT_BRANDING_COLOR;
	const finalTargetElement = targetElement || document.documentElement;

	try {
		// Convert to HEX for functions that require it (getLuminance, generateThemeBranding)
		const hexPrimaryColor = toHexColor(primaryColorToUse);
		const primaryTextColor = readableTextColor(hexPrimaryColor);

		// Keep original format for CSS custom property (works with any valid CSS color)
		finalTargetElement.style.setProperty(HUB_PRIMARY_COLOR_CSS_VAR, primaryColorToUse);
		finalTargetElement.style.setProperty(HUB_PRIMARY_TEXT_COLOR_CSS_VAR, primaryTextColor);

		// generateThemeBranding expects HEX colors
		const themeColours = generateThemeBranding(hexPrimaryColor);
		finalTargetElement.style.setProperty(HUB_PRIMARY_LINK_COLOR_CSS_VAR, themeColours.color900);
	} catch (error) {
		fireErrorAnalytics({
			error:
				error instanceof Error
					? error
					: new Error(`Hub branding style processing failed for color: ${primaryColorToUse}`),
			meta: {
				id: 'setHubBrandingStyle',
				packageName: '@atlassian/company-hub-common',
			},
		});
		// Fallback to default branding if color processing fails
		finalTargetElement.style.setProperty(HUB_PRIMARY_COLOR_CSS_VAR, DEFAULT_BRANDING_COLOR);
		finalTargetElement.style.setProperty(HUB_PRIMARY_TEXT_COLOR_CSS_VAR, token('color.text'));
	}

	return (): void => {
		finalTargetElement.style.removeProperty(HUB_PRIMARY_COLOR_CSS_VAR);
		finalTargetElement.style.removeProperty(HUB_PRIMARY_TEXT_COLOR_CSS_VAR);
		finalTargetElement.style.removeProperty(HUB_PRIMARY_LINK_COLOR_CSS_VAR);
	};
};
